import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTrackBackground, Range } from 'react-range';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Button, Form, Image, Modal, OverlayTrigger, Tooltip as BootstrapToolTip } from 'react-bootstrap';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import Select from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import { useAppDispatch } from '../../../store/hooks';
import SpinnerLoader from '../../common/SpinnerLoader';
import { backBtn } from '../../../assets/images/images';
import { NUMBERS } from '../../../resources/number-constants';
import { getIndustryType } from '../../../store/features/auth';
import { candidateSelectionGraphData, candidateSelectionMilesGraphData, getCertificates } from '../../../store/features/dashboard';
import { requestWithParam } from '../../../utility/apiClients';
import { APIS_METHOD } from '../../../resources/api-constants';
import { getStoredDistance, setStoredDistance, clearStoredDistance } from '../../../utility/distancePrefs';
import { getStoredLocation, setStoredLocation, clearStoredLocation } from '../../../utility/locationPrefs';
import CustomRangeSlider from '../../common/CustomRangeSlider';
import ZeroCandidatesModal from '../../modals/ZeroCandidatesModal';
import { extractListItemsFromHTML } from '../../../utility/helpers';
import { createCandidateSelectionChartData, createCandidateSelectionChartOptions } from '../../../utility/chartUtils';
import {
  buildLocationFields,
  CachedCandidateFilters,
  extractAppliedFiltersPayload,
  extractGraphResponseMeta,
  extractGraphSeries,
  extractRawAppliedFiltersSlice,
} from '../../../utility/candidateFilterState';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip, // Chart.js Tooltip remains registered
  Legend,
  Filler
);

type JobTitleFilterEntry = {
  title: string;
  yearsInRoleMin: string;
  yearsInRoleMax: string;
  currentOrLast12Months: boolean;
};

type IndustryExperienceEntry = {
  industry: string;
  yearsMin: string;
  yearsMax: string;
  currentOrLast12Months: boolean;
};

type CompanyFilterEntry = {
  name: string;
  currentOrLast12Months: boolean;
};

type FilterSectionKey = 'location' | 'jobTitle' | 'companyHistory' | 'industry' | 'certification' | 'more';

/** Candidate selection "Percent Fit" slider step (must match `<Range step={...} />`). */
const PERCENT_FIT_STEP = 0.01;

/**
 * Snap a percent-fit value to the slider step grid (2 decimal places).
 * Uses `toFixed` to avoid binary float tails like 90.22000000000001 in UI/state passed to `react-range`.
 */
const snapPercentFitToStep = (n: number): number =>
  parseFloat((Math.round(n / PERCENT_FIT_STEP) * PERCENT_FIT_STEP).toFixed(2));

/**
 * Cumulative graph buckets may repeat the same percentage_fit with increasing counts.
 * At an exact fit, use the maximum number_of_candidates.
 * Between buckets, use the next higher (ceiling) bucket — e.g. past 85.18% toward 90.22%
 * shows the 90.22% count (1), not the 85.18% count (2). Above all buckets, keep the last.
 */
const findCandidateCountAtFit = (targetValue: number, data: Array<{ percentage_fit: number; number_of_candidates?: number }>): number => {
  if (!data.length) return 0;
  const snapped = snapPercentFitToStep(targetValue);
  const countAtFit = (fit: number) => {
    const rows = data.filter((r) => snapPercentFitToStep(r.percentage_fit) === fit);
    if (!rows.length) return 0;
    return Math.max(...rows.map((r) => Number(r.number_of_candidates) || 0));
  };

  const exact = countAtFit(snapped);
  if (exact > 0 || data.some((r) => snapPercentFitToStep(r.percentage_fit) === snapped)) {
    return exact;
  }

  // Ceiling: next bucket at or above the thumb (stricter / fewer candidates as fit rises).
  const fitsAtOrAbove = data
    .map((r) => snapPercentFitToStep(r.percentage_fit))
    .filter((fit) => fit >= snapped);
  if (fitsAtOrAbove.length > 0) {
    return countAtFit(Math.min(...fitsAtOrAbove));
  }

  // Past the last (highest) fit — keep that bucket's count.
  const maxFit = Math.max(...data.map((r) => snapPercentFitToStep(r.percentage_fit)));
  return countAtFit(maxFit);
};

/** Min/max for the percent-fit slider and chart x-axis, quantized to {@link PERCENT_FIT_STEP}. */
const calculatePercentFitMinMax = (data: Array<{ percentage_fit: number }>): { min: number; max: number } => {
  let min = 0;
  let max = NUMBERS.HUNDRED;

  if (data.length > 0) {
    const rawMin = Math.min(...data.map((item) => item.percentage_fit));
    const rawMax = Math.max(...data.map((item) => item.percentage_fit));

    // Quantize bounds to the same grid as the slider thumb; otherwise `values[0]` can be < `min`
    // when API returns values like 52.21 but the thumb snaps to 52.2.
    min = snapPercentFitToStep(Math.floor(rawMin / PERCENT_FIT_STEP) * PERCENT_FIT_STEP);
    max = snapPercentFitToStep(Math.ceil(rawMax / PERCENT_FIT_STEP) * PERCENT_FIT_STEP);

    if (min === max) {
      max = snapPercentFitToStep(max + NUMBERS.FIFTY);
    }
  }

  return { min, max };
};

const clampExpYears = (raw: number): number => Math.min(50, Math.max(0, raw));

/** Map 0–50 total years to 0–100 for dual-thumb track gradient. */
const totalYearsToTrackBackgroundValues = (minY: number, maxY: number): [number, number] => {
  const lo = clampExpYears(minY);
  const hi = clampExpYears(maxY);
  return [(lo / 50) * 100, (hi / 50) * 100];
};

const clamp0to50 = (raw: number): number => Math.min(50, Math.max(0, raw));

/** Parse years string for 0–50 sliders; empty → null (unspecified). */
const parseYears0to50 = (s: string): number | null => {
  const t = (s || '').trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return clamp0to50(n);
};

/** Ordered min/max for API (0–50); empty strings treated as 0. */
const orderedYearRange = (minS: string, maxS: string): { lo: number; hi: number } => {
  const a = parseYears0to50(minS);
  const b = parseYears0to50(maxS);
  const u = a ?? 0;
  const v = b ?? 0;
  return u <= v ? { lo: u, hi: v } : { lo: v, hi: u };
};

const toTitleCase = (input: string) => {
  const s = (input || '').trim();
  if (!s) return s;
  return s
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) => {
      const w = word.trim();
      if (!w) return w;
      if (w.length <= 3 && w === w.toUpperCase()) return w;
      const lower = w.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

// `colors` must have length values.length + 1 (react-range getTrackBackground). Dual-thumb → 3 stops: inactive, accent, inactive.
const renderAdvancedYearsSliderTrack =
  (valuesForBackground: number[], colors: string[]) =>
  ({ props, children }: { props: any; children: React.ReactNode }) =>
    (
      <div
        {...props}
        className="advanced-years-track"
        style={{
          ...props.style,
          background: 'transparent',
        }}
      >
        <div
          className="advanced-years-track-line"
          aria-hidden
          style={{
            height: 2,
            flexShrink: 0,
            width: '100%',
            borderRadius: 999,
            pointerEvents: 'none',
            background: getTrackBackground({
              values: valuesForBackground,
              colors,
              min: 0,
              max: 100,
            }),
          }}
        />
        {children}
      </div>
    );

const coerceExpYear = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(String(v).replace(/[^\d.+-]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const normalizeCertificationValue = (item: unknown): string => {
  if (typeof item === 'string') return item.trim();
  if (item && typeof item === 'object') {
    const candidate = item as Record<string, unknown>;
    const value =
      candidate.certification_name ??
      candidate.name ??
      candidate.title ??
      candidate.label ??
      candidate.value;
    return String(value || '').trim();
  }
  return String(item || '').trim();
};

const normalizeCertificationList = (source: unknown): string[] => {
  const list = Array.isArray(source) ? source : (source ? [source] : []);
  const seen = new Set<string>();
  const normalized: string[] = [];
  list.forEach((item) => {
    const value = normalizeCertificationValue(item);
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push(value);
  });
  return normalized;
};

/**
 * Builds the `filters` object for the candidate search API contract.
 * All year values are passed in years; the backend converts to months.
 */
const buildFilters = (
  jobTitles: JobTitleFilterEntry[],
  industryEntries: IndustryExperienceEntry[],
  companyEntries: CompanyFilterEntry[],
  experienceMin: number,
  experienceMax: number,
  toggles: {
    has_publications: boolean;
    has_volunteering: boolean;
    has_test_scores: boolean;
    has_projects: boolean;
  },
): Record<string, any> => {
  const filters: Record<string, any> = {};

  // 1) Total experience range (slider)
  if (experienceMin > 0 || experienceMax > 0) {
    filters.total_experience_min_years = experienceMin;
    filters.total_experience_max_years = experienceMax;
  }

  // 2) Job title filters (structured) — per-title current + optional years range (0–100)
  const structuredTitleFilters = jobTitles
    .filter((e) => e.title)
    .map((e) => {
      const out: Record<string, any> = {
        name: e.title,
        current_or_last_12_months: !!e.currentOrLast12Months,
      };
      const { lo, hi } = orderedYearRange(e.yearsInRoleMin, e.yearsInRoleMax);
      if (hi > 0) {
        out.years_max_years = hi;
        if (lo > 0) out.years_min_years = lo;
        out.years = hi;
      }
      return out;
    });
  if (structuredTitleFilters.length > 0) {
    filters.job_title_filters = structuredTitleFilters;
  }

  // 3) Company filters (structured) — per-company current flag
  const structuredCompanyFilters = companyEntries
    .filter((c) => c.name)
    .map((c) => ({
      name: c.name,
      ...(c.currentOrLast12Months ? { current_or_last_12_months: true } : {}),
    }));
  if (structuredCompanyFilters.length > 0) {
    filters.companies_worked_in = structuredCompanyFilters;
  }

  // 4) Role experience filters — entries with years range → AND match on role_experience keys
  const roleExpFilters = jobTitles
    .filter((e) => e.title && orderedYearRange(e.yearsInRoleMin, e.yearsInRoleMax).hi > 0)
    .map((e) => {
      const { lo, hi } = orderedYearRange(e.yearsInRoleMin, e.yearsInRoleMax);
      const row: Record<string, any> = { name: e.title, years: hi, years_max_years: hi };
      if (lo > 0) row.years_min_years = lo;
      return row;
    });
  if (roleExpFilters.length > 0) {
    filters.role_experience_filters = roleExpFilters;
  }

  // 5) Industry experience filters — AND match on industry_experience keys (years range 0–100)
  const industryExpFilters = industryEntries
    .filter((e) => e.industry)
    .map((e) => {
      const { lo, hi } = orderedYearRange(e.yearsMin, e.yearsMax);
      const row: Record<string, any> = {
        name: e.industry,
        ...(e.currentOrLast12Months ? { current_or_last_12_months: true } : {}),
      };
      if (hi > 0) {
        row.years_max_years = hi;
        if (lo > 0) row.years_min_years = lo;
        row.years = hi;
      }
      return row;
    });
  if (industryExpFilters.length > 0) {
    filters.industry_experience_filters = industryExpFilters;
  }

  // 6) Boolean toggles (always explicit)
  filters.has_publications = !!toggles.has_publications;
  filters.has_volunteering = !!toggles.has_volunteering;
  filters.has_test_scores = !!toggles.has_test_scores;
  filters.has_projects = !!toggles.has_projects;

  return filters;
};

const CandidateSelection = ({
  jobId,
  setPercentageFitValue,
  aboutFor,
  jobDistance,
  loading,
  jobLocation,
  jobCertificates,
  setCandidateLoading,
  onCachedFiltersChange,
}: any) => {
  const darkThemeVar = 'theme-dark';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [graphData, setGraphData] = useState<any>([]);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [selectedCandidates, setSelectedCandidates] = useState<number>(0);
  /** Ignore out-of-order graph-generate / distance-matching responses. */
  const graphRequestIdRef = useRef(0);
  const [trackColors, setTrackColors] = useState<[string, string]>([
    '#3dcbb1',
    'rgba(41,41,41,0.1)',
  ]);
  // Three gradient stops for dual-thumb sliders (job/industry years + total experience).
  const [experienceRangeTrackColors, setExperienceRangeTrackColors] = useState<
    [string, string, string]
  >(['rgba(41,41,41,0.1)', '#3dcbb1', 'rgba(41,41,41,0.1)']);

  const [loadGraph, setLoadGraph] = useState(false);
  const [showZeroCandidatesModal, setShowZeroCandidatesModal] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [advancedLocations, setAdvancedLocations] = useState<string[]>([]);
  const [advancedLocationInput, setAdvancedLocationInput] = useState<string>("");

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showUnaddedFilterWarning, setShowUnaddedFilterWarning] = useState(false);
  /** True when the warning was opened from closing the filters modal (X / backdrop); shows "Discard and close". */
  const [unaddedFilterWarningFromCloseAttempt, setUnaddedFilterWarningFromCloseAttempt] =
    useState(false);
  const [advancedJobTitles, setAdvancedJobTitles] = useState<JobTitleFilterEntry[]>([]);
  const [advancedCompaniesWorkedIn, setAdvancedCompaniesWorkedIn] = useState<CompanyFilterEntry[]>([]);
  const [advancedJobTitleInput, setAdvancedJobTitleInput] = useState<string>("");
  const [advancedJobTitleYearsMin, setAdvancedJobTitleYearsMin] = useState<string>("");
  const [advancedJobTitleYearsMax, setAdvancedJobTitleYearsMax] = useState<string>("");
  const [advancedJobTitleCurrentOrLast12Months, setAdvancedJobTitleCurrentOrLast12Months] = useState<boolean>(true);
  const [advancedCompanyInput, setAdvancedCompanyInput] = useState<string>('');
  const [advancedCompanyCurrentOrLast12Months, setAdvancedCompanyCurrentOrLast12Months] = useState<boolean>(true);
  const [advancedIndustryExperienceEntries, setAdvancedIndustryExperienceEntries] = useState<IndustryExperienceEntry[]>([]);
  const [advancedIndustryOption, setAdvancedIndustryOption] = useState<any>(null);
  const [advancedIndustryYearsMin, setAdvancedIndustryYearsMin] = useState<string>("");
  const [advancedIndustryYearsMax, setAdvancedIndustryYearsMax] = useState<string>("");
  const [advancedIndustryCurrentOrLast12Months, setAdvancedIndustryCurrentOrLast12Months] = useState<boolean>(true);
  const [advancedExperienceMin, setAdvancedExperienceMin] = useState<number>(0);
  const [advancedExperienceMax, setAdvancedExperienceMax] = useState<number>(0);
  /** Last applied experience range (mirrors miles `currentDistance` → modal slider). */
  const [appliedExperienceMin, setAppliedExperienceMin] = useState<number>(0);
  const [appliedExperienceMax, setAppliedExperienceMax] = useState<number>(0);
  const [advancedExperienceMinInput, setAdvancedExperienceMinInput] = useState<string>("0");
  const [advancedExperienceMaxInput, setAdvancedExperienceMaxInput] = useState<string>("0");
  const [isExperienceMinFocused, setIsExperienceMinFocused] = useState<boolean>(false);
  const [isExperienceMaxFocused, setIsExperienceMaxFocused] = useState<boolean>(false);
  const [showJobTitleYearsTooltip, setShowJobTitleYearsTooltip] = useState<boolean>(false);
  const [showIndustryYearsTooltip, setShowIndustryYearsTooltip] = useState<boolean>(false);
  const [showTotalExperienceYearsTooltip, setShowTotalExperienceYearsTooltip] = useState<boolean>(false);
  const [industryTypeOptions, setIndustryTypeOptions] = useState<any[]>([]);
  const [advancedMiles, setAdvancedMiles] = useState<number | null>(null);
  const [advancedHasPublications, setAdvancedHasPublications] = useState<boolean>(false);
  const [advancedHasVolunteering, setAdvancedHasVolunteering] = useState<boolean>(false);
  const [advancedHasTestScores, setAdvancedHasTestScores] = useState<boolean>(false);
  const [advancedHasProjects, setAdvancedHasProjects] = useState<boolean>(false);
  const [advancedCertifications, setAdvancedCertifications] = useState<string[]>([]);
  const [advancedCertificationInput, setAdvancedCertificationInput] = useState<string>('');
  const [certificationMenuIsOpen, setCertificationMenuIsOpen] = useState(false);
  const [expandedFilterSections, setExpandedFilterSections] = useState<Record<FilterSectionKey, boolean>>({
    location: true,
    jobTitle: false,
    companyHistory: false,
    industry: false,
    certification: false,
    more: false,
  });

  const notifyCachedFilters = useCallback((savedData: unknown) => {
    const normalized = extractAppliedFiltersPayload(savedData);
    onCachedFiltersChange?.(normalized);
    return normalized;
  }, [onCachedFiltersChange]);

  const applySavedFiltersFromApi = useCallback((savedData: any) => {
    const filters = savedData?.filters && typeof savedData.filters === 'object' ? savedData.filters : {};

    const normalizeYears = (raw: any): { min: string; max: string } => {
      const minVal = coerceExpYear(raw?.years_min_years ?? raw?.years_min ?? raw?.min_years);
      const maxVal = coerceExpYear(raw?.years_max_years ?? raw?.years_max ?? raw?.years ?? raw?.role_years ?? raw?.industry_years ?? raw?.max_years);
      return {
        min: minVal !== null && minVal > 0 ? String(clamp0to50(minVal)) : "",
        max: maxVal !== null && maxVal > 0 ? String(clamp0to50(maxVal)) : "",
      };
    };

    const titleByKey = new Map<string, JobTitleFilterEntry>();
    const baseTitleFilters = Array.isArray(filters?.job_title_filters) ? filters.job_title_filters : [];
    baseTitleFilters.forEach((item: any) => {
      const rawTitle =
        typeof item === 'string'
          ? item
          : String(item?.name || item?.title || item?.role_title_normalised || '').trim();
      const title = toTitleCase(rawTitle);
      if (!title) return;
      const years = normalizeYears(item);
      const currentOrLast12Months =
        item?.current_or_last_12_months !== undefined ? !!item.current_or_last_12_months : true;
      titleByKey.set(title.toLowerCase(), {
        title,
        yearsInRoleMin: years.min,
        yearsInRoleMax: years.max,
        currentOrLast12Months,
      });
    });
    const roleExperienceFilters = Array.isArray(filters?.role_experience_filters) ? filters.role_experience_filters : [];
    roleExperienceFilters.forEach((item: any) => {
      const rawTitle = String(item?.name || item?.title || item?.role_title_normalised || '').trim();
      const title = toTitleCase(rawTitle);
      if (!title) return;
      const years = normalizeYears(item);
      const currentOrLast12Months =
        item?.current_or_last_12_months !== undefined ? !!item.current_or_last_12_months : true;
      titleByKey.set(title.toLowerCase(), {
        title,
        yearsInRoleMin: years.min,
        yearsInRoleMax: years.max,
        currentOrLast12Months,
      });
    });
    const mappedJobTitles = Array.from(titleByKey.values());

    const mappedCompanies: CompanyFilterEntry[] = (Array.isArray(filters?.companies_worked_in) ? filters.companies_worked_in : [])
      .map((item: any) => ({
        name: String(typeof item === 'string' ? item : (item?.name || item?.company || '')).trim(),
        currentOrLast12Months:
          typeof item === 'object' && item?.current_or_last_12_months !== undefined
            ? !!item.current_or_last_12_months
            : true,
      }))
      .filter((item: CompanyFilterEntry) => item.name);

    const mappedIndustryExperience: IndustryExperienceEntry[] = (Array.isArray(filters?.industry_experience_filters)
      ? filters.industry_experience_filters
      : [])
      .map((item: any) => {
        const years = normalizeYears(item);
        return {
          industry: String(item?.name || item?.industry || item?.industry_name || '').trim(),
          yearsMin: years.min,
          yearsMax: years.max,
          currentOrLast12Months:
            item?.current_or_last_12_months !== undefined
              ? !!item.current_or_last_12_months
              : true,
        };
      })
      .filter((item: IndustryExperienceEntry) => item.industry);
    const mappedCertifications: string[] = normalizeCertificationList(
      filters?.certifications
      ?? filters?.certification_filters
      ?? filters?.certification
      ?? filters?.certificate
      ?? savedData?.certifications
      ?? savedData?.certification
      ?? savedData?.certificate
    );

    const locationCandidates = Array.isArray(savedData?.cities)
      ? savedData.cities
      : (savedData?.city ? [savedData.city] : []);
    const mappedLocations: string[] = locationCandidates
      .map((item: any) => String(item || '').trim())
      .filter(Boolean);

    const expMin = clampExpYears(coerceExpYear(filters?.total_experience_min_years) ?? 0);
    const expMax = clampExpYears(
      coerceExpYear(filters?.total_experience_max_years) ??
      coerceExpYear(filters?.total_experience_years) ??
      0
    );

    const orderedExp = expMin <= expMax ? { min: expMin, max: expMax } : { min: expMax, max: expMin };
    const parsedDistance = coerceExpYear(savedData?.distance_in_miles);
    const distanceInMiles = parsedDistance !== null ? Math.max(0, parsedDistance) : null;

    setAdvancedJobTitles(mappedJobTitles);
    setAdvancedCompaniesWorkedIn(mappedCompanies);
    setAdvancedIndustryExperienceEntries(mappedIndustryExperience);
    setAdvancedCertifications(mappedCertifications);
    setAdvancedLocations(mappedLocations);
    setAdvancedMiles(distanceInMiles);
    setAdvancedHasPublications(!!filters?.has_publications);
    setAdvancedHasVolunteering(!!filters?.has_volunteering);
    setAdvancedHasTestScores(!!filters?.has_test_scores);
    setAdvancedHasProjects(!!filters?.has_projects);
    setAdvancedExperienceMin(orderedExp.min);
    setAdvancedExperienceMax(orderedExp.max);
    setAppliedExperienceMin(orderedExp.min);
    setAppliedExperienceMax(orderedExp.max);
  }, []);

  const hydrateAppliedFilters = useCallback((savedData: unknown) => {
    applySavedFiltersFromApi(extractRawAppliedFiltersSlice(savedData));
    return notifyCachedFilters(savedData);
  }, [applySavedFiltersFromApi, notifyCachedFilters]);

  const fetchSavedCandidateFilters = useCallback(async (): Promise<{ hasSavedFilters: boolean; hasExperienceFilter: boolean; cached: CachedCandidateFilters }> => {
    if (!jobId) return { hasSavedFilters: false, hasExperienceFilter: false, cached: { filters: {}, cities: [] } };
    try {
      const response = await requestWithParam(
        APIS_METHOD.GET,
        '/job-candidate/candidate-saved-filters/',
        {},
        { job: jobId, source: 'cached' },
        false
      );
      const savedData = response?.data?.data ?? response?.data;
      applySavedFiltersFromApi(extractRawAppliedFiltersSlice(savedData));
      const cached = notifyCachedFilters(savedData);
      const filters = cached.filters ?? {};
      const hasExperienceFilter =
        filters?.total_experience_min_years !== undefined ||
        filters?.total_experience_max_years !== undefined ||
        filters?.total_experience_years !== undefined;
      return { hasSavedFilters: !!cached.has_saved_filters, hasExperienceFilter, cached };
    } catch (error) {
      console.error('Error fetching candidate saved filters:', error);
      return { hasSavedFilters: false, hasExperienceFilter: false, cached: { filters: {}, cities: [] } };
    }
  }, [jobId, applySavedFiltersFromApi, notifyCachedFilters]);

  useEffect(() => {
    if (selectedLocation) {
      setAdvancedLocationInput(selectedLocation);
      setAdvancedLocations((prev) => (prev.includes(selectedLocation) ? prev : [...prev, selectedLocation]));
    } else {
      setAdvancedLocationInput("");
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (!isExperienceMinFocused) {
      setAdvancedExperienceMinInput(advancedExperienceMin >= 50 ? "50+" : String(advancedExperienceMin));
    }
  }, [advancedExperienceMin, isExperienceMinFocused]);

  useEffect(() => {
    if (!isExperienceMaxFocused) {
      setAdvancedExperienceMaxInput(advancedExperienceMax >= 50 ? "50+" : String(advancedExperienceMax));
    }
  }, [advancedExperienceMax, isExperienceMaxFocused]);

  const addUniqueItem = (list: string[], value: string) => {
    const cleaned = (value || "").trim();
    if (!cleaned) return list;
    if (list.some((item) => item.toLowerCase() === cleaned.toLowerCase())) return list;
    return [...list, cleaned];
  };

  const addUniqueCompany = (list: CompanyFilterEntry[], entry: CompanyFilterEntry) => {
    const cleaned = (entry?.name || '').trim();
    if (!cleaned) return list;
    if (list.some((item) => item.name.toLowerCase() === cleaned.toLowerCase())) return list;
    return [...list, { name: cleaned, currentOrLast12Months: !!entry.currentOrLast12Months }];
  };

  const addCertificationFromInput = () => {
    const value = advancedCertificationInput.trim();
    if (!value) return;
    setAdvancedCertifications((prev) => addUniqueItem(prev, value));
    setAdvancedCertificationInput('');
  };

  const loadCertificationOptions = useCallback(async (inputValue: string) => {
    const query = (inputValue || '').trim();
    if (query.length < 3) return [];
    try {
      const res: any = await dispatch(getCertificates({ search: query, page: 1, limit: 10 })).unwrap();
      const list: string[] = Array.isArray(res?.data?.results) ? res.data.results : [];
      return list.map((item) => ({ label: item, value: item }));
    } catch {
      return [];
    }
  }, [dispatch]);

  const hasActiveLocationFilters = advancedLocations.length > 0 || advancedMiles !== null;
  const hasActiveJobTitleFilters = advancedJobTitles.length > 0;
  const hasActiveCompanyHistoryFilters =
    advancedCompaniesWorkedIn.length > 0 || advancedExperienceMin > 0 || advancedExperienceMax > 0;
  const hasActiveIndustryFilters = advancedIndustryExperienceEntries.length > 0;
  const hasActiveCertificationFilters = advancedCertifications.length > 0;
  const hasActiveMoreFilters =
    advancedHasPublications || advancedHasVolunteering || advancedHasTestScores || advancedHasProjects;

  const toggleFilterSection = (section: FilterSectionKey) => {
    setExpandedFilterSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const expandFilterSection = (section: FilterSectionKey) => {
    setExpandedFilterSections((prev) => {
      if (prev[section]) return prev;
      return {
        ...prev,
        [section]: true,
      };
    });
  };

  const companyAutocompleteTimerRef = useRef<number | null>(null);
  const companyAutocompleteSeqRef = useRef(0);
  const loadCompanyOptions = useCallback((inputValue: string) => {
    const q = (inputValue || '').trim();
    const minChars = 2;
    const debounceMs = 350;

    // Reset pending debounce timer (if any).
    if (companyAutocompleteTimerRef.current) {
      window.clearTimeout(companyAutocompleteTimerRef.current);
      companyAutocompleteTimerRef.current = null;
    }

    // For empty/short input, don't call API.
    if (!q || q.length < minChars) {
      // Return no options until user types enough chars.
      return Promise.resolve([]);
    }

    const seq = ++companyAutocompleteSeqRef.current;

    return new Promise<any[]>((resolve) => {
      companyAutocompleteTimerRef.current = window.setTimeout(async () => {
        try {
          const res = await requestWithParam(
            APIS_METHOD.GET,
            '/job-candidate/company-autocomplete/',
            {},
            { search: q, limit: 50 },
            false
          );

          // Ignore stale responses (user typed again).
          if (seq !== companyAutocompleteSeqRef.current) return;

          const raw = res?.data;
          const list = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.data)
              ? raw.data
              : Array.isArray(raw?.data?.results)
                ? raw.data.results
                : Array.isArray(raw?.results)
                  ? raw.results
                  : [];
          const options = list
            .map((item: any) => {
              const name =
                typeof item === 'string'
                  ? item
                  : String(item?.text || item?.name || item?.id || item?.value || item?.label || '').trim();
              if (!name) return null;
              return { value: name, label: toTitleCase(name) };
            })
            .filter(Boolean);
          resolve(options as any[]);
        } catch {
          if (seq !== companyAutocompleteSeqRef.current) return;
          resolve([]);
        }
      }, debounceMs);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (companyAutocompleteTimerRef.current) {
        window.clearTimeout(companyAutocompleteTimerRef.current);
      }
    };
  }, []);

  const jobTitleAutocompleteTimerRef = useRef<number | null>(null);
  const jobTitleAutocompleteSeqRef = useRef(0);
  const loadJobTitleOptions = useCallback((inputValue: string) => {
    const q = (inputValue || '').trim();
    const minChars = 2;
    const debounceMs = 350;

    if (jobTitleAutocompleteTimerRef.current) {
      window.clearTimeout(jobTitleAutocompleteTimerRef.current);
      jobTitleAutocompleteTimerRef.current = null;
    }

    if (!q || q.length < minChars) {
      return Promise.resolve([]);
    }

    const seq = ++jobTitleAutocompleteSeqRef.current;

    return new Promise<any[]>((resolve) => {
      jobTitleAutocompleteTimerRef.current = window.setTimeout(async () => {
        try {
          const res = await requestWithParam(
            APIS_METHOD.GET,
            '/job-candidate/job-title-autocomplete/',
            {},
            { search: q, limit: 30 },
            false
          );

          if (seq !== jobTitleAutocompleteSeqRef.current) return;

          const raw = res?.data;
          const body = raw?.data !== undefined && raw?.data !== null ? raw.data : raw;
          const list = Array.isArray(body?.results)
            ? body.results
            : Array.isArray(raw?.results)
              ? raw.results
              : Array.isArray(raw?.data)
                ? raw.data
                : [];
          const options = list
            .map((item: any) => {
              const name =
                typeof item === 'string'
                  ? item
                  : String(item?.text || item?.id || item?.name || item?.value || item?.label || '').trim();
              if (!name) return null;
              return { value: name, label: toTitleCase(name) };
            })
            .filter(Boolean);
          resolve(options as any[]);
        } catch {
          if (seq !== jobTitleAutocompleteSeqRef.current) return;
          resolve([]);
        }
      }, debounceMs);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (jobTitleAutocompleteTimerRef.current) {
        window.clearTimeout(jobTitleAutocompleteTimerRef.current);
      }
    };
  }, []);

  const clampExperienceScore = clampExpYears;

  const addJobTitle = () => {
    const normalizedTitle = advancedJobTitleInput.trim();
    if (!normalizedTitle) return;
    setAdvancedJobTitles((prev) => {
      if (prev.some((item) => item.title.toLowerCase() === normalizedTitle.toLowerCase())) {
        return prev;
      }
      return [
        ...prev,
        {
          title: normalizedTitle,
          yearsInRoleMin: advancedJobTitleYearsMin.trim(),
          yearsInRoleMax: advancedJobTitleYearsMax.trim(),
          currentOrLast12Months: advancedJobTitleCurrentOrLast12Months,
        },
      ];
    });
    setAdvancedJobTitleInput("");
    setAdvancedJobTitleYearsMin("");
    setAdvancedJobTitleYearsMax("");
    setAdvancedJobTitleCurrentOrLast12Months(true);
  };

  const addCompanyFromInput = () => {
    const name = advancedCompanyInput.trim();
    if (!name) return;
    setAdvancedCompaniesWorkedIn((prev) =>
      addUniqueCompany(prev, {
        name,
        currentOrLast12Months: advancedCompanyCurrentOrLast12Months,
      })
    );
    setAdvancedCompanyCurrentOrLast12Months(true);
    setAdvancedCompanyInput('');
  };

  /** Single-value display for AsyncCreatableSelect — `value={null}` hides the label after picking from the menu. */
  const advancedJobTitleCreatableValue = useMemo(
    () =>
      advancedJobTitleInput.trim()
        ? { label: advancedJobTitleInput, value: advancedJobTitleInput }
        : null,
    [advancedJobTitleInput],
  );

  const advancedCompanyCreatableValue = useMemo(
    () =>
      advancedCompanyInput.trim()
        ? { label: advancedCompanyInput, value: advancedCompanyInput }
        : null,
    [advancedCompanyInput],
  );

  useEffect(() => {
    const fetchIndustryTypes = async () => {
      try {
        const industryRes: any = await dispatch(getIndustryType()).unwrap();
        const options = Array.isArray(industryRes?.data)
          ? industryRes.data.map(({ id, name }: any) => ({ value: id, label: name }))
          : [];
        setIndustryTypeOptions([{ value: null, label: 'None' }, ...options]);
      } catch (error) {
        console.info('Error in fetching industry types', error);
      }
    };
    fetchIndustryTypes();
  }, [dispatch]);

  useEffect(() => {
    if (!showAdvancedFilters) return;
    setExpandedFilterSections({
      location: hasActiveLocationFilters,
      jobTitle: hasActiveJobTitleFilters,
      companyHistory: hasActiveCompanyHistoryFilters,
      industry: hasActiveIndustryFilters,
      // Keep certification section visible by default so users can verify
      // selected certifications without needing to manually expand it.
      certification: true,
      more: hasActiveMoreFilters,
    });
  }, [
    showAdvancedFilters,
    hasActiveLocationFilters,
    hasActiveJobTitleFilters,
    hasActiveCompanyHistoryFilters,
    hasActiveIndustryFilters,
    hasActiveCertificationFilters,
    hasActiveMoreFilters,
  ]);

  const hasUnaddedFilterEntry = useCallback(() => {
    if (advancedJobTitleInput.trim() !== '') return true;
    if (advancedCompanyInput.trim() !== '') return true;
    if (advancedIndustryOption != null && advancedIndustryOption.value != null) return true;
    if (advancedCertificationInput.trim() !== '') return true;
    return false;
  }, [advancedJobTitleInput, advancedCompanyInput, advancedIndustryOption, advancedCertificationInput]);

  /** Which section(s) still need Add — drives warning title + copy. */
  const unaddedFilterScenario = useMemo(():
    | 'jobTitle'
    | 'industry'
    | 'company'
    | 'certification'
    | 'multiple'
    | null => {
    const pendingJobTitle = advancedJobTitleInput.trim() !== '';
    const pendingCompany = advancedCompanyInput.trim() !== '';
    const pendingIndustry =
      advancedIndustryOption != null && advancedIndustryOption.value != null;
    const pendingCertification = advancedCertificationInput.trim() !== '';
    const count = [pendingJobTitle, pendingCompany, pendingIndustry, pendingCertification].filter(Boolean).length;
    if (count === 0) return null;
    if (count > 1) return 'multiple';
    if (pendingJobTitle) return 'jobTitle';
    if (pendingCompany) return 'company';
    if (pendingIndustry) return 'industry';
    if (pendingCertification) return 'certification';
    return null;
  }, [advancedJobTitleInput, advancedCompanyInput, advancedIndustryOption, advancedCertificationInput]);

  const unaddedFilterWarningTitle = useMemo(() => {
    switch (unaddedFilterScenario) {
      case 'jobTitle':
        return 'Job title not added';
      case 'company':
        return 'Company not added';
      case 'industry':
        return 'Industry not added';
      case 'certification':
        return 'Certification not added';
      case 'multiple':
        return 'Filters not added';
      default:
        return 'Filter not added';
    }
  }, [unaddedFilterScenario]);

  const unaddedFilterWarningBody = useMemo(() => {
    switch (unaddedFilterScenario) {
      case 'jobTitle':
        return 'Click Add to save your job title, or clear the field.';
      case 'company':
        return 'Click Add to save the company, pick a suggestion, or clear the field.';
      case 'industry':
        return 'Click Add to save your industry, or clear the selection.';
      case 'certification':
        return 'Click Add to save the certification, pick a suggestion, or clear the field.';
      case 'multiple':
        return 'Click Add in each section to save, or clear those fields.';
      default:
        return 'Click Add to save, or clear the fields.';
    }
  }, [unaddedFilterScenario]);

  const discardUnaddedFilterFields = useCallback(() => {
    setAdvancedJobTitleInput('');
    setAdvancedJobTitleYearsMin('');
    setAdvancedJobTitleYearsMax('');
    setAdvancedJobTitleCurrentOrLast12Months(true);
    setAdvancedCompanyInput('');
    setAdvancedIndustryOption(null);
    setAdvancedIndustryYearsMin('');
    setAdvancedIndustryYearsMax('');
    setAdvancedIndustryCurrentOrLast12Months(true);
    setAdvancedCertificationInput('');
  }, []);

  const addIndustryExperience = () => {
    const industry = String(advancedIndustryOption?.label || "").trim();
    if (!industry) return;
    setAdvancedIndustryExperienceEntries((prev) => {
      if (prev.some((item) => item.industry.toLowerCase() === industry.toLowerCase())) {
        return prev;
      }
      return [
        ...prev,
        {
          industry,
          yearsMin: advancedIndustryYearsMin.trim(),
          yearsMax: advancedIndustryYearsMax.trim(),
          currentOrLast12Months: advancedIndustryCurrentOrLast12Months,
        },
      ];
    });
    setAdvancedIndustryOption(null);
    setAdvancedIndustryYearsMin("");
    setAdvancedIndustryYearsMax("");
    setAdvancedIndustryCurrentOrLast12Months(true);
  };

  const fetchGraphByLocation = (payload: Record<string, any>) => {
    const requestId = ++graphRequestIdRef.current;
    setLoadGraph(true);
    setGraphData([]);

    dispatch(candidateSelectionMilesGraphData(payload))
      .unwrap()
      .then((res: any) => {
        if (requestId !== graphRequestIdRef.current) return;
        const { points, body } = extractGraphSeries(res);
        const filteredRecords = getHighPercentageFitRecords(points);
        const meta = extractGraphResponseMeta(res);
        processGraphDataResponse(filteredRecords, meta.selectedPercentageFit ?? undefined);
        if (meta.sliderCandidateCount !== null) {
          setSelectedCandidates(meta.sliderCandidateCount);
        }
        hydrateAppliedFilters(body);
        setLoadGraph(false);
      })
      .catch((error: any) => {
        if (requestId !== graphRequestIdRef.current) return;
        console.error('Error fetching graph details:', error);
        setLoadGraph(false);
      });
  };

  const handleApplyAdvancedFilters = () => {
    const pendingCertification = advancedCertificationInput.trim();
    const normalizedPendingCertification = normalizeCertificationValue(pendingCertification);
    const pendingUnaddedNonCertification =
      advancedJobTitleInput.trim() !== '' ||
      advancedCompanyInput.trim() !== '' ||
      (advancedIndustryOption != null && advancedIndustryOption.value != null);
    if (pendingUnaddedNonCertification) {
      setUnaddedFilterWarningFromCloseAttempt(false);
      setShowUnaddedFilterWarning(true);
      return;
    }
    const effectiveAdvancedCertifications = normalizedPendingCertification
      ? addUniqueItem(advancedCertifications, normalizedPendingCertification)
      : advancedCertifications;
    if (normalizedPendingCertification) {
      setAdvancedCertifications(effectiveAdvancedCertifications);
      setAdvancedCertificationInput('');
    }
    const appliedDistance = advancedMiles !== null ? advancedMiles : null;
    const normalizedCity = cleanLocationText((advancedLocations[0] || selectedLocation || ""));

    // --- Build location fields (multi-location support) ---
    const effectiveLocations = advancedLocations.length > 0
      ? advancedLocations
      : (normalizedCity ? [normalizedCity] : []);

    const locationFields = buildLocationFields(effectiveLocations);

    // --- Build filters object per API contract (full replace) ---
    const filtersObj = buildFilters(
      advancedJobTitles,
      advancedIndustryExperienceEntries,
      advancedCompaniesWorkedIn,
      advancedExperienceMin,
      advancedExperienceMax,
      {
        has_publications: advancedHasPublications,
        has_volunteering: advancedHasVolunteering,
        has_test_scores: advancedHasTestScores,
        has_projects: advancedHasProjects,
      }
    );
    filtersObj.certifications = effectiveAdvancedCertifications;

    // --- Full API payload for the graph/distance endpoint ---
    const apiPayload: Record<string, any> = {
      job: jobId,
      distance_in_miles: appliedDistance,
      ...locationFields,
      filters: filtersObj,
      filters_replace: true,
    };

    setSelectedLocation(normalizedCity);
    setStoredLocation(jobId, normalizedCity);
    setCurrentDistance(appliedDistance);
    setStoredDistance(jobId, appliedDistance);
    setAppliedExperienceMin(advancedExperienceMin);
    setAppliedExperienceMax(advancedExperienceMax);

    fetchGraphByLocation(apiPayload);
    setShowAdvancedFilters(false);
  };

  const handleAdvancedFiltersModalHide = () => {
    if (hasUnaddedFilterEntry()) {
      setUnaddedFilterWarningFromCloseAttempt(true);
      setShowUnaddedFilterWarning(true);
      return;
    }
    setShowAdvancedFilters(false);
  };

  const dismissUnaddedFilterWarning = () => {
    setShowUnaddedFilterWarning(false);
    setUnaddedFilterWarningFromCloseAttempt(false);
  };

  /** Clears pending job title / company / industry fields and closes only the warning (filters modal stays open). */
  const discardUnaddedFieldsAndDismissWarning = () => {
    discardUnaddedFilterFields();
    dismissUnaddedFilterWarning();
  };

  const discardUnaddedAndCloseFiltersModal = () => {
    discardUnaddedFilterFields();
    dismissUnaddedFilterWarning();
    setShowAdvancedFilters(false);
  };

  const handleClearAdvancedFilters = () => {
    setAdvancedJobTitles([]);
    setAdvancedCompaniesWorkedIn([]);
    setAdvancedJobTitleInput("");
    setAdvancedJobTitleYearsMin("");
    setAdvancedJobTitleYearsMax("");
    setAdvancedJobTitleCurrentOrLast12Months(true);
    setAdvancedCompanyInput('');
    setAdvancedCompanyCurrentOrLast12Months(true);
    setAdvancedIndustryExperienceEntries([]);
    setAdvancedIndustryOption(null);
    setAdvancedIndustryYearsMin("");
    setAdvancedIndustryYearsMax("");
    setAdvancedIndustryCurrentOrLast12Months(true);
    setAdvancedExperienceMin(0);
    setAdvancedExperienceMax(0);
    setAppliedExperienceMin(0);
    setAppliedExperienceMax(0);
    setAdvancedMiles(null);
    setAdvancedHasPublications(false);
    setAdvancedHasVolunteering(false);
    setAdvancedHasTestScores(false);
    setAdvancedHasProjects(false);
    setAdvancedCertifications([]);
    setAdvancedCertificationInput('');
    setAdvancedLocations([]);
    setAdvancedLocationInput("");
    setSelectedLocation('');
    setCurrentDistance(null);
    clearStoredDistance(jobId);
    clearStoredLocation(jobId);

    fetchGraphByLocation({
      job: jobId,
      distance_in_miles: null,
      cities: [],
      filters: {},
      filters_replace: true,
    });
  };
  // Initialize currentDistance with stored distance or jobDistance prop
  const [currentDistance, setCurrentDistance] = useState<number | null>(() => {
    const stored = getStoredDistance(jobId);
    return stored !== undefined ? stored : (jobDistance ?? null);
  });
  const googleMapAPIKey = `${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`;

  useEffect(() => {
    if (showAdvancedFilters) {
      setAdvancedMiles(currentDistance ?? null);
      setAdvancedExperienceMin(appliedExperienceMin);
      setAdvancedExperienceMax(appliedExperienceMax);
    }
  }, [showAdvancedFilters, currentDistance, appliedExperienceMin, appliedExperienceMax]);

  const cleanLocationText = (text: string) => {
    if (!text) return text;
    // Remove leading labels like "Located in", "Location:", "Location -"
    let out = text.replace(/^\s*(located\s+in|location\s*[:-])\s*/i, '');
    // Collapse extra whitespace
    out = out.replace(/\s+/g, ' ').trim();
    return out;
  };

  // Function to check for the theme-dark class and update track colors accordingly
  const updateTrackColors = () => {
    const dark = document.body.classList.contains(darkThemeVar);
    // One palette for job/industry years, Miles, percent-fit, and total experience (outer segments).
    const accent = dark ? '#3DCBB1' : '#3dcbb1';
    const inactive = dark ? 'rgba(255,255,255,0.48)' : 'rgba(41,41,41,0.1)';
    setTrackColors([accent, inactive]);
    setExperienceRangeTrackColors([inactive, accent, inactive]);
  };

  const fitAxis = useMemo(() => calculatePercentFitMinMax(graphData), [graphData]);

  // Remount Chart.js when the series identity changes so scales/points cannot stick to a prior job/cache.
  const chartRevision = useMemo(() => {
    if (!graphData.length) return 'empty';
    const first = graphData[0];
    const last = graphData[graphData.length - 1];
    return [
      graphData.length,
      first?.percentage_fit,
      last?.percentage_fit,
      first?.number_of_candidates,
      last?.number_of_candidates,
    ].join(':');
  }, [graphData]);

  // Prepare the chart data and options using utility functions
  const data = createCandidateSelectionChartData(graphData);
  const options = useMemo(
    () => createCandidateSelectionChartOptions(fitAxis.min, fitAxis.max),
    [fitAxis.min, fitAxis.max]
  );

  // Helper function to filter records with percentage_fit <= 100
  const getHighPercentageFitRecords = (data: any) => {
    if (!Array.isArray(data)) {
      throw new Error("Input must be an array of objects");
    }
    return data.filter(record => record.percentage_fit <= 100);
  };

  // Helper function to process and set graph data response
  const processGraphDataResponse = useCallback((filteredRecords: any[], savedPercentageFit?: number) => {
    const sortedRecords = [...filteredRecords].sort((a, b) => a.percentage_fit - b.percentage_fit);
    setGraphData(sortedRecords);
    if (sortedRecords.length > 0) {
      const { min, max } = calculatePercentFitMinMax(sortedRecords);
      const targetFit =
        savedPercentageFit !== undefined && savedPercentageFit !== null
          ? savedPercentageFit
          : sortedRecords[0].percentage_fit;
      const snapped = snapPercentFitToStep(Math.min(max, Math.max(min, targetFit)));
      setSliderValue(snapped);
      // Keep payload in sync with the thumb label (do not remap to a distant graph bucket).
      setPercentageFitValue(snapped);
    } else {
      setSliderValue(0);
      setPercentageFitValue(0);
    }
  }, [setPercentageFitValue]);

  const fetchGraphDetails = useCallback(async () => {
    const requestId = ++graphRequestIdRef.current;
    try {
      setLoadGraph(true);
      setGraphData([]);
      let needsRegenerate = false;
      try {
        const storageKey = `job:${jobId}:needsRegenerate`;
        needsRegenerate = localStorage.getItem(storageKey) === 'true';
      } catch {
        // ignore storage errors
      }

      const graphPayload: any = needsRegenerate ? { job: jobId, force: true } : { job: jobId };

      const [graphRes] = await Promise.all([
        dispatch(candidateSelectionGraphData(graphPayload)).unwrap(),
        fetchSavedCandidateFilters(),
      ]);

      if (requestId !== graphRequestIdRef.current) return;

      const { points } = extractGraphSeries(graphRes);
      const filteredRecords = getHighPercentageFitRecords(points);
      const meta = extractGraphResponseMeta(graphRes);
      processGraphDataResponse(filteredRecords, meta.selectedPercentageFit ?? undefined);
      if (meta.sliderCandidateCount !== null) {
        setSelectedCandidates(meta.sliderCandidateCount);
      }

      if (needsRegenerate) {
        try {
          localStorage.removeItem(`job:${jobId}:needsRegenerate`);
        } catch {
          // ignore storage errors
        }
      }
    } catch (error) {
      if (requestId !== graphRequestIdRef.current) return;
      console.error('Error fetching graph details:', error);
    } finally {
      if (requestId === graphRequestIdRef.current) {
        setLoadGraph(false);
        setHasFetched(true);
      }
    }
  }, [dispatch, jobId, processGraphDataResponse, fetchSavedCandidateFilters]);

  const handleSliderChange = (values: number[]) => {
    if (!graphData.length) return;

    const { min, max } = fitAxis;
    const clamped = Math.min(max, Math.max(min, values[0]));
    const snappedThumb = snapPercentFitToStep(clamped);
    setSliderValue(snappedThumb);
    // Send the same value shown on the thumb (e.g. 94.9), not the nearest graph x-bucket (e.g. 93).
    setPercentageFitValue(snappedThumb);
  };

  const navigateTo = (route: any) => {
    navigate(route);
  };

  useEffect(() => {
    if (!hasFetched || loadGraph) return;

    if (!graphData.length) {
      setSelectedCandidates(0);
      setShowZeroCandidatesModal(true);
      return;
    }

    setSelectedCandidates(findCandidateCountAtFit(sliderValue, graphData));
  }, [sliderValue, graphData, hasFetched, loadGraph]);

  useEffect(() => {
    if (jobId) {
      fetchGraphDetails();
    }
  }, [jobId, fetchGraphDetails]);

  // Sync currentDistance when jobId or jobDistance changes
  useEffect(() => {
    const stored = getStoredDistance(jobId);
    if (stored !== undefined) {
      setCurrentDistance(stored);
    } else {
      setCurrentDistance(jobDistance ?? null);
    }
  }, [jobId, jobDistance]);

  useEffect(() => {
    updateTrackColors(); // Set initial color based on the current theme
    const observer = new MutationObserver(updateTrackColors);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (showAdvancedFilters) {
      updateTrackColors();
    }
  }, [showAdvancedFilters]);

  useEffect(() => {
    if (setCandidateLoading) {
      setCandidateLoading(loadGraph);
    }
  }, [loadGraph, setCandidateLoading]);

  useEffect(() => {
    return () => {
      setCandidateLoading?.(false);
    };
  }, [setCandidateLoading]);

  // Prefill Location: prefer stored value, else derive from job description
  useEffect(() => {
    // 1) Stored per-job location takes precedence
    const stored = getStoredLocation(jobId);
    if (stored) {
      setSelectedLocation(stored);
      return;
    }
    // 2) Fallback to job description location
    // Always try to extract location from jobLocation, even if it might be empty
    // This ensures location is populated regardless of distance setting
    if (jobLocation) {
      let city = '';
      try {
        const items = extractListItemsFromHTML(String(jobLocation));
        const cleaned = (items || []).map((i) => cleanLocationText(i)).filter(Boolean);
        const unique = Array.from(new Set(cleaned));
        city = unique[0] || '';
      } catch (e) {
        city = cleanLocationText(String(jobLocation) || '');
      }
      if (city) {
        setSelectedLocation(city);
      }
    }
  }, [jobId, jobLocation]);

  const extraPixels = () => {
    const totalCounts = Math.max(
      ...graphData.map((item: any) => item.number_of_candidates)
    );
    if (totalCounts < 99) {
      return 0;
    } else if (totalCounts > 99 && totalCounts < 999) {
      return 7;
    } else if (totalCounts > 999 && totalCounts < 9999) {
      return 16;
    } else if (totalCounts > 9999 && totalCounts < 99999) {
      return 21;
    } else if (totalCounts > 99999 && totalCounts < 999999) {
      return 28;
    } else if (totalCounts > 999999) {
      return 35;
    }
    return 0;
  };

  const extraMargin = () => {
    const totalCounts = Math.max(
      ...graphData.map((item: any) => item.number_of_candidates)
    );
    if (totalCounts < 99) {
      return 0;
    } else if (totalCounts > 99 && totalCounts < 999) {
      return 9;
    } else if (totalCounts > 999 && totalCounts < 9999) {
      return 0;
    }
    return 0;
  };


  return (
    <>
      <div className="outer-section">
        <OverlayTrigger
          placement="top"
          overlay={
            <BootstrapToolTip>
              Back
            </BootstrapToolTip>
          }
        >
          <div className="back-btn" onClick={() => navigateTo(`/job-description-summary?jobId=${jobId}&about=${aboutFor}`)}>
            <Image
              src={backBtn}
              height={25}
              width={25}
              alt="Back"
              className="back-btn-arrow"
            />
          </div>
        </OverlayTrigger>
      </div>
      <div className='candidate-selection-header'>
        <div className="main-page-title-container">
          <div className="preview-title">
            <span className="mobile-inline-back-btn d-md-none">
              <span
                className="back-btn candidate-back"
                role="button"
                tabIndex={0}
                onClick={() => navigateTo(`/job-description-summary?jobId=${jobId}&about=${aboutFor}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigateTo(`/job-description-summary?jobId=${jobId}&about=${aboutFor}`);
                  }
                }}
              >
                <Image
                  src={backBtn}
                  height={25}
                  width={25}
                  alt="Back"
                  className="back-btn-arrow"
                />
              </span>
            </span>
            Select Percentage Fit <span>.</span>
          </div>
          <p className="preview-subtitle candidate-selection-mobile-subtitle">
            Set miles, Select percentage fit & review matching candidate pool.
          </p>
        </div>

        <Modal
          show={showAdvancedFilters}
          onHide={handleAdvancedFiltersModalHide}
          centered
          className="advanced-filters-modal"
        >
          <Modal.Header>
            <Button
              variant="cancel"
              className="btn-cancel btn-cancel-filled advanced-filters-top-close-btn"
              onClick={handleAdvancedFiltersModalHide}
            >
              Close
            </Button>
            <Modal.Title className="advanced-filters-modal-title">Filters</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div className="advanced-filters-grid">
                <div
                  className={`advanced-filters-field advanced-location-filter-group ${expandedFilterSections.location ? 'is-expanded' : 'is-collapsed'}`}
                  onClick={() => {
                    if (!expandedFilterSections.location) expandFilterSection('location');
                  }}
                >
                  <button
                    type="button"
                    className="advanced-filter-section-toggle"
                    onClick={() => toggleFilterSection('location')}
                  >
                    <span className="advanced-location-filter-heading">Location Section</span>
                    <span className={`advanced-filter-section-arrow ${expandedFilterSections.location ? 'is-open' : ''}`}>▾</span>
                  </button>
                  {expandedFilterSections.location && (
                  <div className="advanced-location-filter-row">
                    <div className="advanced-location-filter-col">
                      <Form.Label>Location</Form.Label>
                      <GooglePlacesAutocomplete
                        apiKey={googleMapAPIKey}
                        selectProps={{
                          value: advancedLocationInput ? { label: advancedLocationInput, value: advancedLocationInput } as any : null,
                          onChange: (option: any) => {
                            const label = option?.label || option?.value?.description || '';
                            const city = cleanLocationText(label || '');
                            setAdvancedLocationInput(city);
                            setAdvancedLocations((prev) => addUniqueItem(prev, city));
                          },
                          placeholder: 'Type City Name',
                          noOptionsMessage: () => 'Type City Name',
                          loadingMessage: () => 'Searching…',
                          openMenuOnFocus: true,
                          className: 'google-select-box',
                          classNamePrefix: 'google-select',
                          menuPortalTarget: document.body,
                          menuPosition: 'fixed',
                          menuPlacement: 'auto',
                          styles: {
                            menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                            menu: (base: any) => ({ ...base, zIndex: 2000 }),
                          },
                        }}
                        autocompletionRequest={{
                          types: ['(cities)'],
                          componentRestrictions: { country: ['us'] },
                        }}
                      />
                      {advancedLocations.length > 0 && (
                        <div className="advanced-selected-pills">
                          {advancedLocations.map((loc) => (
                            <button
                              type="button"
                              key={`loc-${loc}`}
                              className="advanced-pill"
                              onClick={() => setAdvancedLocations((prev) => prev.filter((item) => item !== loc))}
                              title="Remove filter"
                            >
                              {loc} <span className="pill-x">x</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="advanced-location-filter-col">
                      <Form.Label>Miles</Form.Label>
                      <div className="advanced-filters-mile-slider">
                        <CustomRangeSlider
                          initialValue={advancedMiles}
                          onChange={(value: number | null) => setAdvancedMiles(value)}
                          trackColors={trackColors}
                        />
                      </div>
                    </div>
                  </div>
                  )}
                </div>

                <div
                  className={`advanced-filters-field advanced-job-title-filter-group ${expandedFilterSections.jobTitle ? 'is-expanded' : 'is-collapsed'}`}
                  onClick={() => {
                    if (!expandedFilterSections.jobTitle) expandFilterSection('jobTitle');
                  }}
                >
                  <button
                    type="button"
                    className="advanced-filter-section-toggle"
                    onClick={() => toggleFilterSection('jobTitle')}
                  >
                    <span className="advanced-job-title-filter-heading">Job Title Section</span>
                    <span className={`advanced-filter-section-arrow ${expandedFilterSections.jobTitle ? 'is-open' : ''}`}>▾</span>
                  </button>
                  {expandedFilterSections.jobTitle && (
                  <>
                  <div className="advanced-job-title-filter-row">
                    <div className="advanced-job-title-filter-col">
                      <Form.Label>Job Title</Form.Label>
                      <AsyncCreatableSelect
                        classNamePrefix="google-select"
                        className="google-select-box"
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadJobTitleOptions as any}
                        // Do not pass `inputValue`: controlling it to the selected label keeps react-select's
                        // internal input non-empty, so it hides SingleValue and sets the input opacity to 0
                        // (empty-looking field with clear × until blur). Sync typing via onInputChange only.
                        onInputChange={(v, meta) => {
                          if (meta.action === 'input-change') {
                            setAdvancedJobTitleInput(v);
                          }
                        }}
                        blurInputOnSelect
                        onChange={(option: any) => {
                          if (!option) {
                            setAdvancedJobTitleInput('');
                            return;
                          }
                          const v = String(option.label ?? option.value ?? '').trim();
                          if (v) setAdvancedJobTitleInput(v);
                        }}
                        value={advancedJobTitleCreatableValue}
                        isClearable
                        placeholder="Type Job Title"
                        openMenuOnFocus
                        noOptionsMessage={({ inputValue }) =>
                          (inputValue || '').trim().length < 2 ? 'Type Job Title' : 'No matches'
                        }
                        loadingMessage={() => 'Searching…'}
                        formatCreateLabel={(input) => `Use "${input}"`}
                        createOptionPosition="first"
                        allowCreateWhileLoading
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                          menu: (base: any) => ({ ...base, zIndex: 2000 }),
                        }}
                      />
                    </div>
                    <div className="advanced-job-title-filter-col">
                      <Form.Label>Years in Role (range)</Form.Label>
                      <div className="advanced-years-slider-field">
                        <div className="advanced-years-slider-row">
                          <div className="advanced-years-slider-wrap">
                            <Range
                              step={1}
                              min={0}
                              max={50}
                              values={(() => {
                                const { lo, hi } = orderedYearRange(
                                  advancedJobTitleYearsMin,
                                  advancedJobTitleYearsMax,
                                );
                                return [lo, hi];
                              })()}
                              onChange={(values) => {
                                const a = clamp0to50(values[0]);
                                const b = clamp0to50(values[1]);
                                const lo = Math.min(a, b);
                                const hi = Math.max(a, b);
                                setAdvancedJobTitleYearsMin(lo === 0 ? '' : String(lo));
                                setAdvancedJobTitleYearsMax(hi === 0 ? '' : String(hi));
                              }}
                              renderTrack={renderAdvancedYearsSliderTrack(
                                (() => {
                                  const { lo, hi } = orderedYearRange(
                                    advancedJobTitleYearsMin,
                                    advancedJobTitleYearsMax,
                                  );
                                  return [lo, hi];
                                })(),
                                experienceRangeTrackColors,
                              )}
                              renderThumb={({ props, index, isDragged }) => {
                                const { lo, hi } = orderedYearRange(
                                  advancedJobTitleYearsMin,
                                  advancedJobTitleYearsMax,
                                );
                                const tip = index === 0 ? lo : hi;
                                return (
                                  <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                      <BootstrapToolTip id="years-role-tooltip" className="bs-tooltip">
                                        {tip}
                                      </BootstrapToolTip>
                                    }
                                    show={showJobTitleYearsTooltip || isDragged}
                                  >
                                    <div
                                      {...props}
                                      className={`advanced-years-thumb ${isDragged ? 'is-dragged' : ''}`}
                                      onMouseEnter={() => setShowJobTitleYearsTooltip(true)}
                                      onMouseLeave={() => setShowJobTitleYearsTooltip(false)}
                                    />
                                  </OverlayTrigger>
                                );
                              }}
                            />
                            <div className="advanced-years-ends">
                              <span>0</span>
                              <span>50</span>
                            </div>
                          </div>
                          <div className="advanced-years-range-inputs">
                            <Form.Control
                              type="number"
                              min={0}
                              max={50}
                              value={advancedJobTitleYearsMin}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim() === '') {
                                  setAdvancedJobTitleYearsMin('');
                                  return;
                                }
                                const next = clamp0to50(Number(raw));
                                setAdvancedJobTitleYearsMin(String(next));
                                const hi = parseYears0to50(advancedJobTitleYearsMax) ?? 0;
                                if (hi > 0 && next > hi) setAdvancedJobTitleYearsMax(String(next));
                              }}
                              placeholder="Min"
                              className="advanced-years-input"
                            />
                            <span className="advanced-years-range-sep" aria-hidden>
                              –
                            </span>
                            <Form.Control
                              type="number"
                              min={0}
                              max={50}
                              value={advancedJobTitleYearsMax}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim() === '') {
                                  setAdvancedJobTitleYearsMax('');
                                  return;
                                }
                                const next = clamp0to50(Number(raw));
                                setAdvancedJobTitleYearsMax(String(next));
                                const lo = parseYears0to50(advancedJobTitleYearsMin) ?? 0;
                                if (next > 0 && next < lo) setAdvancedJobTitleYearsMin(String(next));
                              }}
                              placeholder="Max"
                              className="advanced-years-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="adv-job-title-current-or-last-12"
                    label="Current or within last 12 months"
                    checked={advancedJobTitleCurrentOrLast12Months}
                    onChange={(e) => setAdvancedJobTitleCurrentOrLast12Months(e.target.checked)}
                    className="advanced-job-title-checkbox"
                  />
                  <Button
                    type="button"
                    variant="primary"
                    className="advanced-job-title-add-btn"
                    onClick={addJobTitle}
                    disabled={!advancedJobTitleInput.trim()}
                  >
                    Add
                  </Button>
                  {advancedJobTitles.length > 0 && (
                    <div className="advanced-selected-pills">
                      {advancedJobTitles.map((titleEntry) => (
                        <button
                          type="button"
                          key={`job-${titleEntry.title}`}
                          className="advanced-pill"
                          onClick={() =>
                            setAdvancedJobTitles((prev) =>
                              prev.filter((item) => item.title !== titleEntry.title)
                            )
                          }
                          title={`Job Title: ${titleEntry.title}
Years in Role: ${(() => {
                            const { lo, hi } = orderedYearRange(
                              titleEntry.yearsInRoleMin,
                              titleEntry.yearsInRoleMax,
                            );
                            if (hi <= 0) return 'Not specified';
                            if (lo <= 0) return `0–${hi}`;
                            return `${lo}–${hi}`;
                          })()}
Current/Last 12 Months: ${titleEntry.currentOrLast12Months ? "Yes" : "No"}`}
                        >
                          {titleEntry.title}{' '}
                          {(() => {
                            const { lo, hi } = orderedYearRange(
                              titleEntry.yearsInRoleMin,
                              titleEntry.yearsInRoleMax,
                            );
                            return hi > 0 ? (
                              <span className="advanced-pill-years">
                                ({lo > 0 ? `${lo}–` : ''}
                                {hi}y)
                              </span>
                            ) : null;
                          })()}{' '}
                          <span className="pill-x">x</span>
                        </button>
                      ))}
                    </div>
                  )}
                  </>
                  )}
                </div>

                <div
                  className={`advanced-filters-field advanced-company-history-group ${expandedFilterSections.companyHistory ? 'is-expanded' : 'is-collapsed'}`}
                  onClick={() => {
                    if (!expandedFilterSections.companyHistory) expandFilterSection('companyHistory');
                  }}
                >
                  <button
                    type="button"
                    className="advanced-filter-section-toggle"
                    onClick={() => toggleFilterSection('companyHistory')}
                  >
                    <span className="advanced-company-history-heading">Company History Section</span>
                    <span className={`advanced-filter-section-arrow ${expandedFilterSections.companyHistory ? 'is-open' : ''}`}>▾</span>
                  </button>
                  {expandedFilterSections.companyHistory && (
                  <>
                  <div className="advanced-company-history-row">
                    <div className="advanced-company-history-col">
                      <Form.Label>Companies Worked In</Form.Label>
                      <AsyncCreatableSelect
                        classNamePrefix="google-select"
                        className="google-select-box"
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadCompanyOptions as any}
                        onInputChange={(v, meta) => {
                          if (meta.action === 'input-change') {
                            setAdvancedCompanyInput(v);
                          }
                        }}
                        blurInputOnSelect
                        onChange={(option: any) => {
                          if (!option) {
                            setAdvancedCompanyInput('');
                            return;
                          }
                          const name = String(option.label ?? option.value ?? '').trim();
                          if (name) setAdvancedCompanyInput(name);
                        }}
                        value={advancedCompanyCreatableValue}
                        isClearable
                        placeholder="Type Company Name"
                        openMenuOnFocus
                        noOptionsMessage={({ inputValue }) =>
                          (inputValue || '').trim().length < 2 ? 'Type Company Name' : 'No matches'
                        }
                        loadingMessage={() => 'Searching…'}
                        formatCreateLabel={(input) => `Use "${input}"`}
                        createOptionPosition="first"
                        allowCreateWhileLoading
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                          menu: (base: any) => ({ ...base, zIndex: 2000 }),
                        }}
                      />
                      <Button
                        type="button"
                        variant="primary"
                        className="advanced-job-title-add-btn"
                        onClick={addCompanyFromInput}
                        disabled={!advancedCompanyInput.trim()}
                      >
                        Add
                      </Button>
                    </div>
                    <div className="advanced-company-history-col advanced-company-history-slider-col">
                      <div className="advanced-experience-score-subheading">
                        Total Years of Work Experience
                      </div>
                      <div className="advanced-years-slider-field">
                        <div className="advanced-years-slider-row">
                          <div className="advanced-years-slider-wrap">
                            <Range
                              step={1}
                              min={0}
                              max={50}
                              values={[advancedExperienceMin, advancedExperienceMax]}
                              onChange={(values) => {
                                const a = clampExperienceScore(values[0]);
                                const b = clampExperienceScore(values[1]);
                                setAdvancedExperienceMin(Math.min(a, b));
                                setAdvancedExperienceMax(Math.max(a, b));
                              }}
                              renderTrack={renderAdvancedYearsSliderTrack(
                                totalYearsToTrackBackgroundValues(
                                  advancedExperienceMin,
                                  advancedExperienceMax,
                                ),
                                experienceRangeTrackColors,
                              )}
                              renderThumb={({ props, index, isDragged }) => {
                                const tip =
                                  index === 0 ? advancedExperienceMin : advancedExperienceMax;
                                const tipLabel = tip >= 50 ? "50+" : String(tip);
                                return (
                                  <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                      <BootstrapToolTip
                                        id="years-total-exp-tooltip"
                                        className="bs-tooltip"
                                      >
                                        {tipLabel}
                                      </BootstrapToolTip>
                                    }
                                    show={showTotalExperienceYearsTooltip || isDragged}
                                  >
                                    <div
                                      {...props}
                                      className={`advanced-years-thumb ${isDragged ? 'is-dragged' : ''}`}
                                      onMouseEnter={() => setShowTotalExperienceYearsTooltip(true)}
                                      onMouseLeave={() => setShowTotalExperienceYearsTooltip(false)}
                                    />
                                  </OverlayTrigger>
                                );
                              }}
                            />
                            <div className="advanced-years-ends">
                              <span>0</span>
                              <span>50+</span>
                            </div>
                          </div>
                          <div className="advanced-years-range-inputs advanced-experience-total-years-inputs">
                            <Form.Control
                              type="text"
                              inputMode="numeric"
                              placeholder="Min"
                              value={
                                isExperienceMinFocused
                                  ? advancedExperienceMinInput
                                  : advancedExperienceMin >= 50 ? "50+" : String(advancedExperienceMin)
                              }
                              onFocus={() => {
                                setIsExperienceMinFocused(true);
                                setAdvancedExperienceMinInput(String(advancedExperienceMin));
                              }}
                              onBlur={() => setIsExperienceMinFocused(false)}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setAdvancedExperienceMinInput(raw);
                                const digits = raw.replace(/[^\d]/g, "");
                                if (digits === "") { setAdvancedExperienceMin(0); return; }
                                const next = clampExperienceScore(Number(digits));
                                if (Number.isNaN(next)) return;
                                setAdvancedExperienceMin(next);
                                if (next > advancedExperienceMax) setAdvancedExperienceMax(next);
                              }}
                              className="advanced-years-input"
                            />
                            <span className="advanced-years-range-sep" aria-hidden>
                              –
                            </span>
                            <Form.Control
                              type="text"
                              inputMode="numeric"
                              placeholder="Max"
                              value={
                                isExperienceMaxFocused
                                  ? advancedExperienceMaxInput
                                  : advancedExperienceMax >= 50 ? "50+" : String(advancedExperienceMax)
                              }
                              onFocus={() => {
                                setIsExperienceMaxFocused(true);
                                setAdvancedExperienceMaxInput(String(advancedExperienceMax));
                              }}
                              onBlur={() => setIsExperienceMaxFocused(false)}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setAdvancedExperienceMaxInput(raw);
                                const digits = raw.replace(/[^\d]/g, "");
                                if (digits === "") { setAdvancedExperienceMax(0); return; }
                                const next = clampExperienceScore(Number(digits));
                                if (Number.isNaN(next)) return;
                                setAdvancedExperienceMax(next);
                                if (next < advancedExperienceMin) setAdvancedExperienceMin(next);
                              }}
                              className="advanced-years-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="advanced-company-history-follow-row">
                    <Form.Check
                      type="checkbox"
                      id="adv-company-current-or-last-12"
                      label="Current or within last 12 months"
                      checked={advancedCompanyCurrentOrLast12Months}
                      onChange={(e) => setAdvancedCompanyCurrentOrLast12Months(e.target.checked)}
                      className="advanced-job-title-checkbox"
                    />
                  </div>
                  {advancedCompaniesWorkedIn.length > 0 && (
                    <div className="advanced-selected-pills">
                      {advancedCompaniesWorkedIn.map((company) => (
                        <button
                          type="button"
                          key={`company-${company.name}`}
                          className="advanced-pill"
                          onClick={() => setAdvancedCompaniesWorkedIn((prev) => prev.filter((item) => item.name !== company.name))}
                          title={`Company: ${company.name}\nCurrent/Last 12 Months: ${company.currentOrLast12Months ? "Yes" : "No"}`}
                        >
                          {toTitleCase(company.name)}
                          {company.currentOrLast12Months ? " (current)" : ""} <span className="pill-x">x</span>
                        </button>
                      ))}
                    </div>
                  )}
                  </>
                  )}
                </div>

                <div
                  className={`advanced-filters-field advanced-industry-filter-group ${expandedFilterSections.industry ? 'is-expanded' : 'is-collapsed'}`}
                  onClick={() => {
                    if (!expandedFilterSections.industry) expandFilterSection('industry');
                  }}
                >
                  <button
                    type="button"
                    className="advanced-filter-section-toggle"
                    onClick={() => toggleFilterSection('industry')}
                  >
                    <span className="advanced-industry-filter-heading">Industry Section</span>
                    <span className={`advanced-filter-section-arrow ${expandedFilterSections.industry ? 'is-open' : ''}`}>▾</span>
                  </button>
                  {expandedFilterSections.industry && (
                  <>
                  <div className="advanced-experience-filter-row advanced-experience-industry-row">
                    <div className="advanced-experience-filter-col">
                      <Form.Label>Industry</Form.Label>
                      <Select
                        classNamePrefix="google-select"
                        className="google-select-box"
                        options={industryTypeOptions}
                        value={advancedIndustryOption}
                        onChange={(option: any) => setAdvancedIndustryOption(option?.value ? option : null)}
                        placeholder="Select industry"
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                          menu: (base: any) => ({ ...base, zIndex: 2000 }),
                        }}
                      />
                    </div>
                    <div className="advanced-experience-filter-col">
                      <Form.Label>Years in Industry (range)</Form.Label>
                      <div className="advanced-years-slider-field">
                        <div className="advanced-years-slider-row">
                          <div className="advanced-years-slider-wrap">
                            <Range
                              step={1}
                              min={0}
                              max={50}
                              values={(() => {
                                const { lo, hi } = orderedYearRange(
                                  advancedIndustryYearsMin,
                                  advancedIndustryYearsMax,
                                );
                                return [lo, hi];
                              })()}
                              onChange={(values) => {
                                const a = clamp0to50(values[0]);
                                const b = clamp0to50(values[1]);
                                const lo = Math.min(a, b);
                                const hi = Math.max(a, b);
                                setAdvancedIndustryYearsMin(lo === 0 ? '' : String(lo));
                                setAdvancedIndustryYearsMax(hi === 0 ? '' : String(hi));
                              }}
                              renderTrack={renderAdvancedYearsSliderTrack(
                                (() => {
                                  const { lo, hi } = orderedYearRange(
                                    advancedIndustryYearsMin,
                                    advancedIndustryYearsMax,
                                  );
                                  return [lo, hi];
                                })(),
                                experienceRangeTrackColors,
                              )}
                              renderThumb={({ props, index, isDragged }) => {
                                const { lo, hi } = orderedYearRange(
                                  advancedIndustryYearsMin,
                                  advancedIndustryYearsMax,
                                );
                                const tip = index === 0 ? lo : hi;
                                return (
                                  <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                      <BootstrapToolTip id="years-industry-tooltip" className="bs-tooltip">
                                        {tip}
                                      </BootstrapToolTip>
                                    }
                                    show={showIndustryYearsTooltip || isDragged}
                                  >
                                    <div
                                      {...props}
                                      className={`advanced-years-thumb ${isDragged ? 'is-dragged' : ''}`}
                                      onMouseEnter={() => setShowIndustryYearsTooltip(true)}
                                      onMouseLeave={() => setShowIndustryYearsTooltip(false)}
                                    />
                                  </OverlayTrigger>
                                );
                              }}
                            />
                            <div className="advanced-years-ends">
                              <span>0</span>
                              <span>50</span>
                            </div>
                          </div>
                          <div className="advanced-years-range-inputs">
                            <Form.Control
                              type="number"
                              min={0}
                              max={50}
                              value={advancedIndustryYearsMin}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim() === '') {
                                  setAdvancedIndustryYearsMin('');
                                  return;
                                }
                                const next = clamp0to50(Number(raw));
                                setAdvancedIndustryYearsMin(String(next));
                                const hi = parseYears0to50(advancedIndustryYearsMax) ?? 0;
                                if (hi > 0 && next > hi) setAdvancedIndustryYearsMax(String(next));
                              }}
                              placeholder="Min"
                              className="advanced-years-input"
                            />
                            <span className="advanced-years-range-sep" aria-hidden>
                              –
                            </span>
                            <Form.Control
                              type="number"
                              min={0}
                              max={50}
                              value={advancedIndustryYearsMax}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim() === '') {
                                  setAdvancedIndustryYearsMax('');
                                  return;
                                }
                                const next = clamp0to50(Number(raw));
                                setAdvancedIndustryYearsMax(String(next));
                                const lo = parseYears0to50(advancedIndustryYearsMin) ?? 0;
                                if (next > 0 && next < lo) setAdvancedIndustryYearsMin(String(next));
                              }}
                              placeholder="Max"
                              className="advanced-years-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="adv-industry-current-or-last-12"
                    label="Current or within last 12 months"
                    checked={advancedIndustryCurrentOrLast12Months}
                    onChange={(e) => setAdvancedIndustryCurrentOrLast12Months(e.target.checked)}
                    className="advanced-job-title-checkbox"
                  />
                  <Button
                    type="button"
                    variant="primary"
                    className="advanced-experience-add-btn"
                    onClick={addIndustryExperience}
                    disabled={!advancedIndustryOption?.value}
                  >
                    Add
                  </Button>
                  {advancedIndustryExperienceEntries.length > 0 && (
                    <div className="advanced-selected-pills">
                      {advancedIndustryExperienceEntries.map((entry) => (
                        <button
                          type="button"
                          key={`industry-${entry.industry}`}
                          className="advanced-pill"
                          onClick={() =>
                            setAdvancedIndustryExperienceEntries((prev) =>
                              prev.filter(
                                (item) =>
                                  item.industry !== entry.industry
                              )
                            )
                          }
                          title={`Industry: ${entry.industry}
Years in Industry: ${(() => {
                            const { lo, hi } = orderedYearRange(entry.yearsMin, entry.yearsMax);
                            if (hi <= 0) return 'Not specified';
                            if (lo <= 0) return `0–${hi}`;
                            return `${lo}–${hi}`;
                          })()}
Current/Last 12 Months: ${entry.currentOrLast12Months ? "Yes" : "No"}`}
                        >
                          {entry.industry}
                          {(() => {
                            const { lo, hi } = orderedYearRange(entry.yearsMin, entry.yearsMax);
                            return hi > 0 ? (
                              <span className="advanced-pill-years">
                                {' '}
                                ({lo > 0 ? `${lo}–` : ''}
                                {hi}y)
                              </span>
                            ) : null;
                          })()}
                          {entry.currentOrLast12Months ? ' (current)' : ''}{' '}
                          <span className="pill-x">x</span>
                        </button>
                      ))}
                    </div>
                  )}
                  </>
                  )}
                </div>
                <div
                  className={`advanced-filters-field advanced-certification-filter-group ${expandedFilterSections.certification ? 'is-expanded' : 'is-collapsed'}`}
                  onClick={() => {
                    if (!expandedFilterSections.certification) expandFilterSection('certification');
                  }}
                >
                  <button
                    type="button"
                    className="advanced-filter-section-toggle"
                    onClick={() => toggleFilterSection('certification')}
                  >
                    <span className="advanced-certification-filter-heading">Certification Section</span>
                    <span className={`advanced-filter-section-arrow ${expandedFilterSections.certification ? 'is-open' : ''}`}>▾</span>
                  </button>
                  {expandedFilterSections.certification && (
                    <>
                      <div className="advanced-certification-filter-row">
                        <Form.Label>Certification</Form.Label>
                        <AsyncCreatableSelect
                          classNamePrefix="google-select"
                          className="google-select-box"
                          cacheOptions
                          defaultOptions={false}
                          loadOptions={loadCertificationOptions as any}
                          onMenuOpen={() => setCertificationMenuIsOpen(true)}
                          onMenuClose={() => setCertificationMenuIsOpen(false)}
                          onInputChange={(v, meta) => {
                            if (meta.action === 'input-change') {
                              setAdvancedCertificationInput(v);
                            }
                          }}
                          blurInputOnSelect
                          onChange={(option: any) => {
                            if (!option) {
                              setAdvancedCertificationInput('');
                              return;
                            }
                            const value = String(option.label ?? option.value ?? '').trim();
                            if (value) setAdvancedCertificationInput(value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' || certificationMenuIsOpen) return;
                            e.preventDefault();
                            e.stopPropagation();
                            if (advancedCertificationInput.trim()) {
                              addCertificationFromInput();
                            }
                          }}
                          value={
                            advancedCertificationInput.trim()
                              ? { label: advancedCertificationInput, value: advancedCertificationInput }
                              : null
                          }
                          isClearable
                          placeholder="Type Certification Name"
                          openMenuOnFocus
                          noOptionsMessage={({ inputValue }) =>
                            (inputValue || '').trim().length < 3 ? 'Type at least 3 characters' : 'No matches'
                          }
                          loadingMessage={() => 'Searching…'}
                          formatCreateLabel={(input) => `Use "${input}"`}
                          createOptionPosition="first"
                          allowCreateWhileLoading
                          menuPortalTarget={document.body}
                          menuPlacement="auto"
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                            menu: (base: any) => ({ ...base, zIndex: 2000 }),
                          }}
                        />
                        <Button
                          type="button"
                          variant="primary"
                          className="advanced-job-title-add-btn"
                          onClick={addCertificationFromInput}
                          disabled={!advancedCertificationInput.trim()}
                        >
                          Add
                        </Button>
                      </div>
                      {advancedCertifications.length > 0 && (
                        <div className="advanced-selected-pills">
                          {advancedCertifications.map((certification) => (
                            <button
                              type="button"
                              key={`certification-${certification}`}
                              className="advanced-pill"
                              onClick={() =>
                                setAdvancedCertifications((prev) =>
                                  prev.filter((item) => item.toLowerCase() !== certification.toLowerCase())
                                )
                              }
                              title="Remove filter"
                            >
                              {certification} <span className="pill-x">x</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div
                  className={`advanced-filters-field advanced-more-filter-group ${expandedFilterSections.more ? 'is-expanded' : 'is-collapsed'}`}
                  onClick={() => {
                    if (!expandedFilterSections.more) expandFilterSection('more');
                  }}
                >
                  <button
                    type="button"
                    className="advanced-filter-section-toggle"
                    onClick={() => toggleFilterSection('more')}
                  >
                    <span className="advanced-industry-filter-heading">More</span>
                    <span className={`advanced-filter-section-arrow ${expandedFilterSections.more ? 'is-open' : ''}`}>▾</span>
                  </button>
                  {expandedFilterSections.more && (
                  <>
                  <div className="advanced-company-history-follow-row">
                    <div className="advanced-filters-toggles-grid advanced-company-history-toggles advanced-company-history-toggles-inline advanced-more-toggles-row">
                    <Form.Check
                      type="checkbox"
                      id="adv-publications"
                      label="Publications"
                      checked={advancedHasPublications}
                      onChange={(e) => setAdvancedHasPublications(e.target.checked)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="adv-volunteering"
                      label="Volunteering"
                      checked={advancedHasVolunteering}
                      onChange={(e) => setAdvancedHasVolunteering(e.target.checked)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="adv-test-scores"
                      label="Test Scores"
                      checked={advancedHasTestScores}
                      onChange={(e) => setAdvancedHasTestScores(e.target.checked)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="adv-projects"
                      label="Projects"
                      checked={advancedHasProjects}
                      onChange={(e) => setAdvancedHasProjects(e.target.checked)}
                    />
                    </div>
                  </div>
                  </>
                  )}
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer className="advanced-filters-footer">
            <Button
              type="button"
              variant="link"
              className="advanced-filters-clear-btn"
              onClick={handleClearAdvancedFilters}
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="primary"
              className="advanced-filters-footer-btn"
              onClick={handleApplyAdvancedFilters}
            >
              Apply
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showUnaddedFilterWarning}
          onHide={dismissUnaddedFilterWarning}
          centered
          className="advanced-filters-unadded-warning-modal"
          backdropClassName="advanced-filters-unadded-warning-backdrop"
        >
          <Modal.Header className="advanced-filters-unadded-warning-header">
            <Button
              variant="cancel"
              className="btn-cancel btn-cancel-filled advanced-filters-unadded-top-close-btn"
              onClick={dismissUnaddedFilterWarning}
            >
              Close
            </Button>
            <Modal.Title className="advanced-filters-unadded-warning-title">{unaddedFilterWarningTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="advanced-filters-unadded-warning-body">{unaddedFilterWarningBody}</Modal.Body>
          <Modal.Footer className="advanced-filters-unadded-warning-footer">
            <Button
              variant="link"
              className="advanced-filters-unadded-warning-discard"
              onClick={
                unaddedFilterWarningFromCloseAttempt
                  ? discardUnaddedAndCloseFiltersModal
                  : discardUnaddedFieldsAndDismissWarning
              }
            >
              {unaddedFilterWarningFromCloseAttempt ? 'Discard and close' : 'Discard'}
            </Button>
            <Button
              variant="primary"
              className="advanced-filters-footer-btn advanced-filters-unadded-warning-ok"
              onClick={dismissUnaddedFilterWarning}
            >
              OK
            </Button>
          </Modal.Footer>
        </Modal>

        <div className="main-page-title-container-slider">
          <div className="candidate-top-count d-md-none">
            <strong>{selectedCandidates}</strong> {`Candidate${selectedCandidates > 1 ? 's' : ''}`}
          </div>
          <div className="location-content">
            <Button
              variant="light"
              className="advanced-filters-btn-location"
              disabled={!!loading}
              onClick={() => {
                setShowAdvancedFilters(true);
              }}
            >
                <span className="advanced-filters-btn-inline-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 3H14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="5" cy="3" r="2" fill="currentColor" />
                    <path d="M2 8H14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="10" cy="8" r="2" fill="currentColor" />
                    <path d="M2 13H14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="6" cy="13" r="2" fill="currentColor" />
                  </svg>
                </span>
                Filters
            </Button>
          </div>
        </div>
      </div>
      <div className='graph-mobile-structure'>
        {!loadGraph && (
          <>
            <div className="graph-container-wrapper" >
              <div className='slider-container candidate-priority-slider' style={{ width: '100%' }}>
                <div className="slider-text candidate-priority-label" style={{ width: `${(87 + extraPixels())}px` }}>Percent Fit</div>
                <div className="slider-inner candidate-priority-track" style={{ marginRight: `${(17 + extraMargin())}px` }}>
                  <Range
                    step={PERCENT_FIT_STEP}
                    min={fitAxis.min}
                    max={fitAxis.max}
                    // min={graphData.length > 0 ? Math.min(...graphData.map((item: any) => item.percentage_fit)) : 0}
                    // max={graphData.length > 0 ? Math.max(...graphData.map((item: any) => item.percentage_fit)) : NUMBERS.HUNDRED}
                    values={[sliderValue]}
                    onChange={handleSliderChange}
                    onFinalChange={(values) => {
                      if (!graphData.length) return;
                      const { min, max } = fitAxis;
                      const clamped = Math.min(max, Math.max(min, values[0]));
                      const snappedThumb = snapPercentFitToStep(clamped);
                      if (findCandidateCountAtFit(snappedThumb, graphData) === 0) {
                        setShowZeroCandidatesModal(true);
                      }
                    }}
                    renderTrack={({ props, children }) => (
                      <div
                        {...props}
                        className="track"
                        style={{
                          ...props.style,
                          background: getTrackBackground({
                            values: [sliderValue],
                            colors: trackColors,
                            min: fitAxis.min,
                            max: fitAxis.max,
                          }),
                        }}
                      >
                        {children}
                      </div>
                    )}
                    renderThumb={({ props }) => (
                      <div {...props} className="thumb">
                        <div className="value-box">{snapPercentFitToStep(sliderValue)}%</div>
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="candidate-priority-chart" style={{ width: '100%', height: 300 }}>
                <Line key={chartRevision} data={data} options={options} />
              </div>
            </div>
            <div className="slider-container candidate-priority-count">
              <div className="slider-value">
                <strong>{selectedCandidates}</strong> {`Candidate${selectedCandidates > 1 ? 's' : ''}`}
              </div>
            </div>
          </>
        )}

        {loadGraph && (
          <div className='candidate-loader-lazy'>
            <SpinnerLoader />
            Just a moment! We're loading your results.
          </div>
        )}
      </div>

      <ZeroCandidatesModal
        show={showZeroCandidatesModal}
        onHide={() => setShowZeroCandidatesModal(false)}
      />
    </>
  );
};

export default CandidateSelection;
